import { useState } from "react";
import { IconNameEnum, useModuleBEM } from "@b2bportal/core-themes";
import type { GuestsSelection } from "@b2bportal/lodging-api";
import { DateTimeFormatStyle, useI18nContext } from "@hopper-b2b/i18n";
import { DateRangePicker, GuestPicker } from "@tenant/hotels";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogTrigger,
  Icon,
} from "@tenant/ui";
import dayjs from "dayjs";

import styles from "./OfferSearchBar.module.scss";

export interface OfferSearchValues {
  dateRange: { from?: string; until?: string };
  guests: GuestsSelection;
  rooms: number;
}

export interface OfferSearchBarProps {
  initialValues: OfferSearchValues;
  onSearch: (values: OfferSearchValues) => void;
}

export const OfferSearchBar = ({
  initialValues,
  onSearch,
}: OfferSearchBarProps) => {
  const { t, formatDateTime } = useI18nContext();
  const [block, cn] = useModuleBEM(styles, "OfferSearchBar");

  const [dateRange, setDateRange] = useState(initialValues.dateRange);
  const [guests, setGuests] = useState(initialValues.guests);
  const [rooms, setRooms] = useState(initialValues.rooms);

  const handleSearch = () => onSearch({ dateRange, guests, rooms });

  const pickers = (
    <>
      <DateRangePicker
        startDate={dateRange.from}
        endDate={dateRange.until}
        onStartDateChangeHandler={(date) =>
          setDateRange({ from: date, until: undefined })
        }
        onEndDateChangeHandler={(date) =>
          setDateRange((prev) => ({ ...prev, until: date }))
        }
      />
      <GuestPicker
        guests={guests}
        rooms={rooms}
        onGuestSelectionHandler={(nextGuests, nextRooms) => {
          setGuests(nextGuests);
          if (nextRooms != null) {
            setRooms(nextRooms);
          }
        }}
      />
    </>
  );

  const summary =
    dateRange.from && dateRange.until
      ? t("core-hotels.search.summary", {
          fromDate: formatDateTime(
            dayjs(dateRange.from).toDate(),
            DateTimeFormatStyle.ShortMonthDayShortWeekday
          ),
          untilDate: formatDateTime(
            dayjs(dateRange.until).toDate(),
            DateTimeFormatStyle.ShortMonthDayShortWeekday
          ),
          count: guests.adults + guests.children.length,
        })
      : t("hotelFundedOffersPage.searchDates");

  return (
    <div className={block}>
      <div className={cn("desktop-search")}>
        {pickers}
        <button
          type="button"
          className={cn("desktop-search-button")}
          onClick={handleSearch}
        >
          {t("hotelFundedOffersPage.searchDates")}
        </button>
      </div>

      <Dialog modal={false}>
        <DialogTrigger className={cn("search-button")}>
          <div className={cn("icon")}>
            <Icon iconName={IconNameEnum.search} />
          </div>
          <div className={cn("content")}>
            <div className={cn("search-button-value")}>{summary}</div>
          </div>
        </DialogTrigger>
        <DialogContent
          fullScreen
          title={t("hotelFundedOffersPage.searchDates")}
          bottomFixedContent={
            <DialogClose asChild>
              <button
                type="button"
                className={cn("submit-search-button")}
                onClick={handleSearch}
              >
                {t("hotelFundedOffersPage.searchDates")}
              </button>
            </DialogClose>
          }
        >
          <div className={cn("mobile-fields")}>{pickers}</div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
